import type {
	NativeUploadReconciliationBatch,
	NativeUploadReconciliationItem,
	UploadFailureReason,
	UploadProgressMessage,
	UploadProgressMessageV2,
	UploadProgressPhoto,
	UploadProgressPresentationStage,
	UploadProgressSource,
	UploadProgressStage,
} from 'interfaces/app';

export type UploadProgressDialogProps = {
	photos: string[];
	progressPercentage: number;
	stage: UploadProgressPresentationStage;
	standalone: boolean;
	submittedCount: number;
};

export type ProgressDialogContext = {
	isUploadOnly: boolean;
	isFromExternalUpload: boolean;
	isDialogOpen: boolean;
	/**
	 * Whether the user previously dismissed this external upload's progress
	 * dialog manually. Reset to `false` only when a fresh external upload
	 * session starts (e.g. {@link ExternalUploadController.showQRCode}).
	 */
	wasDismissed: boolean;
};

export type UploadProgressIdentity = {
	batchId: string | null;
	sequence: number;
	stage?: UploadProgressStage;
	submittedCount: number;
	supersededBatchIdentities?: readonly string[];
	uploadSessionId: string | null;
};

const uploadProgressSources: ReadonlySet<UploadProgressSource> = new Set([
	'external-device',
	'native-picker',
]);

const uploadProgressStages: ReadonlySet<UploadProgressStage> = new Set([
	'aborted',
	'completed',
	'failed',
	'processing',
	'saving',
	'selected',
	'transferring',
]);

const uploadFailureReasons: ReadonlySet<UploadFailureReason> = new Set([
	'network',
	'processing',
	'save',
	'unknown',
	'validation',
]);

/**
 * Checks whether a value is a finite, non-negative integer used by progress counts.
 *
 * @param value - Unknown runtime value.
 * @returns Whether the value is a valid count or sequence number.
 */
function isNonNegativeInteger(value: unknown): value is number {
	return (
		typeof value === 'number'
		&& Number.isInteger(value)
		&& value >= 0
	);
}

/**
 * Checks whether an optional numeric native-upload field is finite and non-negative.
 *
 * @param value - Unknown optional numeric value.
 * @returns Whether the value is absent or a supported non-negative number.
 */
function isOptionalNonNegativeNumber(value: unknown): value is number | undefined {
	return (
		typeof value === 'undefined'
		|| (
			typeof value === 'number'
			&& Number.isFinite(value)
			&& value >= 0
		)
	);
}

/**
 * Validates the optional photo list included in an AppSync progress message.
 *
 * @param value - Unknown runtime value.
 * @returns Whether every entry has a supported identifier and optional URL.
 */
function isProgressPhotoArray(value: unknown): value is UploadProgressPhoto[] {
	return (
		Array.isArray(value)
		&& value.every((photo) => (
			isRecord(photo)
			&& (
				typeof photo.id === 'number'
				|| typeof photo.id === 'string'
			)
			&& (
				typeof photo.url === 'undefined'
				|| typeof photo.url === 'string'
			)
		))
	);
}

/**
 * Checks that a terminal payload identifies every submitted photo exactly once.
 *
 * @param value - Unknown terminal photo list.
 * @param submittedCount - Number of photos selected for the batch.
 * @returns Whether the list is complete and contains no duplicate identifiers.
 */
function isCompleteProgressPhotoArray(
	value: unknown,
	submittedCount: number,
): value is UploadProgressPhoto[] {
	return (
		isProgressPhotoArray(value)
		&& value.length === submittedCount
		&& new Set(value.map((photo) => photo.id)).size === submittedCount
	);
}

/**
 * Narrows an unknown JSON value to an object with string keys.
 *
 * @param value - Unknown runtime value.
 * @returns Whether the value is a non-null, non-array object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
	return (
		typeof value === 'object'
		&& value !== null
		&& !Array.isArray(value)
	);
}

/**
 * Validates a protocol-v2 progress source.
 *
 * @param value - Unknown runtime value.
 * @returns Whether the value is a supported progress source.
 */
function isUploadProgressSource(value: unknown): value is UploadProgressSource {
	return (
		typeof value === 'string'
		&& uploadProgressSources.has(value as UploadProgressSource)
	);
}

/**
 * Validates a protocol-v2 upload stage.
 *
 * @param value - Unknown runtime value.
 * @returns Whether the value is a supported upload stage.
 */
function isUploadProgressStage(value: unknown): value is UploadProgressStage {
	return (
		typeof value === 'string'
		&& uploadProgressStages.has(value as UploadProgressStage)
	);
}

function sanitizeProgressPercentage(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.min(
		100,
		Math.max(
			0,
			value,
		),
	);
}

function sanitizeSubmittedCount(value: number): number {
	if (!Number.isFinite(value)) {
		return 0;
	}

	return Math.max(
		0,
		value,
	);
}

function requireExternalUploadId(
	value: string | number | null | undefined,
	label: string,
): string | number {
	if (value === null || value === undefined) {
		throw new Error(`Missing ${label} id for external upload channel`);
	}

	return value;
}

/**
 * Narrows a progress message to the correlated protocol-v2 shape.
 *
 * @param message - Validated progress message.
 * @returns Whether the message carries protocol-v2 correlation fields.
 */
export function isUploadProgressMessageV2(
	message: UploadProgressMessage,
): message is UploadProgressMessageV2 {
	return message.version === 2;
}

/**
 * Produces determinate presentation progress for either protocol version.
 * Protocol v2 may omit percentage during indeterminate stages, so its
 * transferred/submitted counters provide the safe fallback.
 *
 * @param message - Validated upload progress payload.
 * @returns Clamped transfer percentage from zero through one hundred.
 */
export function normalizeUploadProgressPercentage(message: UploadProgressMessage): number {
	if (typeof message.progressPercentage === 'number') {
		return sanitizeProgressPercentage(message.progressPercentage);
	}

	if (!isUploadProgressMessageV2(message)) {
		return 0;
	}

	if (message.submittedCount <= 0) {
		return message.stage === 'completed' ? 100 : 0;
	}

	return sanitizeProgressPercentage(
		100 * message.transferredCount / message.submittedCount,
	);
}

/**
 * Validates an untrusted JSON value before it crosses the AppSync boundary.
 *
 * @param value - Parsed AppSync payload.
 * @returns A typed legacy or protocol-v2 message, or `null` when validation fails.
 */
export function parseUploadProgressMessage(value: unknown): UploadProgressMessage | null {
	if (!isRecord(value)) {
		return null;
	}

	if (
		!isNonNegativeInteger(value.submittedCount)
		|| (
			typeof value.photos !== 'undefined'
			&& !isProgressPhotoArray(value.photos)
		)
		|| (
			typeof value.progressPercentage !== 'undefined'
			&& (
				typeof value.progressPercentage !== 'number'
				|| !Number.isFinite(value.progressPercentage)
				|| value.progressPercentage < 0
				|| value.progressPercentage > 100
			)
		)
	) {
		return null;
	}

	if (
		typeof value.version === 'undefined'
		|| value.version === 1
	) {
		if (typeof value.progressPercentage !== 'number') {
			return null;
		}

		return {
			photos: value.photos,
			progressPercentage: value.progressPercentage,
			submittedCount: value.submittedCount,
			version: value.version,
		};
	}

	if (
		value.version !== 2
		|| typeof value.batchId !== 'string'
		|| value.batchId.length === 0
		|| !isNonNegativeInteger(value.processedCount)
		|| typeof value.sentAt !== 'string'
		|| !Number.isFinite(Date.parse(value.sentAt))
		|| !isNonNegativeInteger(value.sequence)
		|| !isUploadProgressSource(value.source)
		|| !isUploadProgressStage(value.stage)
		|| !isNonNegativeInteger(value.transferredCount)
		|| typeof value.uploadSessionId !== 'string'
		|| value.uploadSessionId.length === 0
		|| value.processedCount > value.submittedCount
		|| value.processedCount > value.transferredCount
		|| value.transferredCount > value.submittedCount
		|| (
			value.stage === 'completed'
			&& (
				value.submittedCount === 0
				|| value.processedCount !== value.submittedCount
				|| value.transferredCount !== value.submittedCount
				|| !isCompleteProgressPhotoArray(
					value.photos,
					value.submittedCount,
				)
			)
		)
		|| (
			typeof value.failureReason !== 'undefined'
			&& (
				typeof value.failureReason !== 'string'
				|| !uploadFailureReasons.has(value.failureReason as UploadFailureReason)
			)
		)
	) {
		return null;
	}

	return {
		batchId: value.batchId,
		failureReason: value.failureReason as UploadFailureReason | undefined,
		photos: value.photos,
		processedCount: value.processedCount,
		...(typeof value.progressPercentage === 'number'
			? {
				progressPercentage: value.progressPercentage,
			}
			: {}),
		sentAt: value.sentAt,
		sequence: value.sequence,
		source: value.source,
		stage: value.stage,
		submittedCount: value.submittedCount,
		transferredCount: value.transferredCount,
		uploadSessionId: value.uploadSessionId,
		version: 2,
	};
}

/**
 * Validates one native item used to rebuild an upload actor after a WebView reload.
 *
 * @param value - Untrusted native bridge payload.
 * @returns A normalized reconciliation item, or `null` when validation fails.
 */
function parseNativeUploadReconciliationItem(value: unknown): NativeUploadReconciliationItem | null {
	if (
		!isRecord(value)
		|| typeof value.uploadId !== 'string'
		|| value.uploadId.length === 0
		|| typeof value.name !== 'string'
		|| typeof value.totalBytes !== 'number'
		|| !Number.isFinite(value.totalBytes)
		|| value.totalBytes <= 0
		|| typeof value.uploadedBytes !== 'number'
		|| !Number.isFinite(value.uploadedBytes)
		|| value.uploadedBytes < 0
		|| value.uploadedBytes > value.totalBytes
		|| (
			typeof value.localPhotoId !== 'undefined'
			&& (
				typeof value.localPhotoId !== 'string'
				|| value.localPhotoId.length === 0
			)
		)
		|| (
			typeof value.uploadUrl !== 'undefined'
			&& (
				typeof value.uploadUrl !== 'string'
				|| value.uploadUrl.length === 0
			)
		)
		|| !isOptionalNonNegativeNumber(value.width)
		|| !isOptionalNonNegativeNumber(value.height)
		|| !isOptionalNonNegativeNumber(value.uploadStart)
	) {
		return null;
	}

	return {
		height: value.height,
		localPhotoId: value.localPhotoId,
		name: value.name,
		totalBytes: value.totalBytes,
		uploadedBytes: value.uploadedBytes,
		uploadId: value.uploadId,
		uploadStart: value.uploadStart,
		uploadUrl: value.uploadUrl,
		width: value.width,
	};
}

/**
 * Validates the authoritative native snapshot required to rebuild upload actors.
 * Non-terminal batches must account for every submitted photo through either an
 * active item or a unique completed photo. Item identifiers must also be unique
 * so one callback cannot ambiguously address multiple actors.
 *
 * @param value - Untrusted native bridge payload.
 * @returns A normalized reconciliation batch, or `null` when validation fails.
 */
export function parseNativeUploadReconciliationBatch(value: unknown): NativeUploadReconciliationBatch | null {
	if (!isRecord(value) || !Array.isArray(value.items)) {
		return null;
	}

	const progress = parseUploadProgressMessage(value.progress);

	if (!progress || !isUploadProgressMessageV2(progress)) {
		return null;
	}

	const items = value.items.map(parseNativeUploadReconciliationItem);

	if (items.some((item) => item === null)) {
		return null;
	}

	const validItems = items as NativeUploadReconciliationItem[];
	const uploadIds = new Set(validItems.map((item) => item.uploadId));
	const localPhotoIds = validItems
		.map((item) => item.localPhotoId)
		.filter((id): id is string => typeof id === 'string');
	const completedPhotoIds = new Set((progress.photos ?? []).map((photo) => (
		`${typeof photo.id}:${photo.id}`
	)));
	const terminal = (
		progress.stage === 'completed'
		|| progress.stage === 'failed'
		|| progress.stage === 'aborted'
	);
	const nonTerminalBatchAccounted = (
		progress.submittedCount > 0
		&& completedPhotoIds.size === (progress.photos?.length ?? 0)
		&& validItems.length + completedPhotoIds.size === progress.submittedCount
	);

	if (
		uploadIds.size !== validItems.length
		|| new Set(localPhotoIds).size !== localPhotoIds.length
		|| (!terminal && !nonTerminalBatchAccounted)
	) {
		return null;
	}

	return {
		items: validItems,
		progress,
	};
}

/**
 * Builds the stable key used to remember a superseded protocol-v2 batch.
 *
 * @param uploadSessionId - Publisher upload-session identity.
 * @param batchId - Batch identity inside the publisher session.
 * @returns Collision-safe serialized identity.
 */
export function getUploadProgressIdentityKey(
	uploadSessionId: string,
	batchId: string,
): string {
	return JSON.stringify([
		uploadSessionId,
		batchId,
	]);
}

/**
 * Rejects legacy ticks after v2 begins and stale sequences within one correlated batch.
 *
 * @param current - Correlation fields stored by the active external actor.
 * @param incoming - Validated progress message being considered.
 * @returns Whether the incoming message may advance or replace the active batch.
 */
export function shouldAcceptUploadProgress(
	current: UploadProgressIdentity,
	incoming: UploadProgressMessage,
): boolean {
	if (!isUploadProgressMessageV2(incoming)) {
		return current.uploadSessionId === null;
	}

	const incomingIdentity = getUploadProgressIdentityKey(
		incoming.uploadSessionId,
		incoming.batchId,
	);

	if (current.supersededBatchIdentities?.includes(incomingIdentity)) {
		return false;
	}

	if (
		current.uploadSessionId !== incoming.uploadSessionId
		|| current.batchId !== incoming.batchId
	) {
		return true;
	}
	if (
		current.stage === 'completed'
		|| current.stage === 'failed'
		|| current.stage === 'aborted'
	) {
		return false;
	}

	return (
		incoming.submittedCount === current.submittedCount
		&& incoming.sequence > current.sequence
	);
}

/**
 * Returns true when the progress dialog should be opened for this tick. The
 * dialog only ever opens for uploadOnly or external-source progress, when
 * there are submitted items still in flight (`progressPercentage < 100`), and
 * when the user has not manually dismissed the dialog already.
 *
 * @param data Validated progress payload.
 * @param context Caller mode, ownership, and dismissal state.
 * @returns Whether this source should create a progress dialog.
 */
export function shouldOpenProgressDialog(
	data: UploadProgressMessage,
	context: ProgressDialogContext,
): boolean {
	const progressPercentage = normalizeUploadProgressPercentage(data);
	const submittedCount = sanitizeSubmittedCount(data.submittedCount);

	if (context.isDialogOpen) {
		return false;
	}

	if (context.wasDismissed) {
		return false;
	}

	if (!submittedCount) {
		return false;
	}

	if (
		isUploadProgressMessageV2(data)
			? (
				data.stage === 'completed'
				|| data.stage === 'failed'
				|| data.stage === 'aborted'
			)
			: progressPercentage >= 100
	) {
		return false;
	}

	return context.isUploadOnly || context.isFromExternalUpload;
}

/**
 * Apply a progress tick to the live reactive props in place. The returned
 * tuple describes which fields actually changed — useful for tests asserting
 * minimal mutation. Photo url comparisons are positional + identity to keep
 * Vue's array-reactivity stable when nothing has actually changed.
 *
 * @param props Reactive dialog state mutated in place.
 * @param data Validated progress payload.
 * @returns Field-level mutation flags for diagnostics/tests.
 */
export function applyProgressUpdate(
	props: UploadProgressDialogProps,
	data: UploadProgressMessage,
): {
	progressPercentageChanged: boolean;
	submittedCountChanged: boolean;
	photosChanged: boolean;
	stageChanged: boolean;
} {
	let progressPercentageChanged = false;
	let submittedCountChanged = false;
	let photosChanged = false;
	let stageChanged = false;
	const progressPercentage = normalizeUploadProgressPercentage(data);

	if (progressPercentage !== props.progressPercentage) {
		props.progressPercentage = progressPercentage;
		progressPercentageChanged = true;
	}

	if (
		typeof data.submittedCount === 'number'
	) {
		const submittedCount = sanitizeSubmittedCount(data.submittedCount);

		if (submittedCount !== props.submittedCount) {
			props.submittedCount = submittedCount;
			submittedCountChanged = true;
		}
	}

	if (data.photos) {
		const photoUrls = data.photos
			.map((photo) => photo.url)
			.filter((url): url is string => !!url);
		const same = (
			photoUrls.length === props.photos.length
			&& photoUrls.every((url, index) => url === props.photos[index])
		);

		if (!same) {
			props.photos = photoUrls;
			photosChanged = true;
		}
	}

	const stage = isUploadProgressMessageV2(data)
		? data.stage
		: data.progressPercentage >= 100
			? 'completed'
			: data.progressPercentage <= 0
				? 'selected'
				: 'transferring';

	if (stage !== props.stage) {
		props.stage = stage;
		stageChanged = true;
	}

	return {
		progressPercentageChanged,
		submittedCountChanged,
		photosChanged,
		stageChanged,
	};
}

export function buildExternalUploadMutation(
	channel: string,
	url: string,
	event: 'upload' | 'complete' | 'abort' | 'close',
	payload: UploadProgressMessage | Record<string, never>,
): {
	type: 'external_upload';
	channel: string;
	url: string;
	event: typeof event;
	payload: typeof payload;
} {
	return {
		type: 'external_upload',
		channel,
		url,
		event,
		payload,
	};
}

export function buildExternalUploadChannel(
	productId: string | number,
	userId: string | number,
): string {
	const safeProductId = requireExternalUploadId(
		productId,
		'product',
	);
	const safeUserId = requireExternalUploadId(
		userId,
		'user',
	);

	return `product/${safeProductId}/external_upload/${safeUserId}`;
}
